import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { MenuItem } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { SaveDataHandler } from 'src/modules/app-template/models/save-data-handler.model';
import { EnaioAssignment } from 'src/modules/enaio/shared/EnaioAssignment';
import { EnaioObjectId } from 'src/modules/enaio/shared/EnaioObjectId';
import { SmTableComponent } from 'src/modules/sm-base/components/sm-table/sm-table.component';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCellButton } from 'src/modules/sm-base/models/table-cell-button.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';
import { BrowserUploadedFile } from 'src/modules/sm-base/models/uploaded-file.model';
import { ProgressorClient } from "src/modules/sm-base/models/progressor-client.model";
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Dates } from "src/modules/utils/shared/dates";
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from 'src/modules/utils/shared/objects';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { Progressor } from "src/modules/utils/misc/Progressor";
import { EnaioDocumentFrontendResult } from '../../netgen/EnaioDocumentFrontendResult';
import { EnaioDocumentResult } from '../../netgen/EnaioDocumentResult';
import { EnaioFrontendInsertData } from '../../netgen/EnaioFrontendInsertData';
import { EnaioDocumentWriteService } from "../../services/enaio-document-write.service";

interface MultiDownloadResult {
    token: string;
    fileName: string;
    requested: number;
    successful: number;
    skipped: number;
    failed: number;
}

@Component({
    selector: 'app-enaio-documents-definition',
    templateUrl: './enaio-documents-definition.component.html',
    styleUrls: ['./enaio-documents-definition.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnaioDocumentsDefinitionComponent extends ComponentView {

    private changeDetectorRef = inject(ChangeDetectorRef);
    private documentWriteService = inject(EnaioDocumentWriteService);

    neededParams: ObjectOfSimpleDataTypes = { defId: "string", parentId: "string?", anonymousGuid: "string?" };
    @Input()
    defId = "";
    @Input()
    parentId = "";
    @Input()
    queryOptions: OrdinaryObject = null;
    @Input()
    selectableDefinitionIds: string[] = null;
    @Output()
    defIdChange = new EventEmitter<string>();

    def: EnaioDocumentFrontendResult = null;
    definitionInfos: EnaioDocumentFrontendResult[] = [];
    documentTypeOptions: FrontendFieldListItem[] = [];
    searchFields: FrontendField[] = [];
    anonymousGuid: string;
    selectedDocument: EnaioDocumentResult = null;
    selectedDocuments: EnaioDocumentResult[] = [];

    allRows: TableRow[];
    table: TableData;
    fullTextSearchTerm = "";
    selection: TableRow[];
    displaySeen = false;
    itemOpened = false;
    invalidToken = false;
    multiDownloadRunning = false;
    multiDownloadProgress: Progressor = null;
    markSeenTokens = new Set<string>();
    toolbarCompact = this.app.isMobile();

    private documentsToolbarElement: HTMLElement;
    private toolbarResizeObserver: ResizeObserver;
    private toolbarMutationObserver: MutationObserver;
    private toolbarMeasureAnimationFrame: number;

    remarkObjects: FrontendFieldListItem[] = null;

    exportMenuItems: MenuItem[] = [];

    input = "";

    @ViewChild("tDocuments")
    tDocuments: SmTableComponent;

    @ViewChild("documentsToolbar", { read: ElementRef })
    set documentsToolbar(value: ElementRef<HTMLElement>) {
        this.initDocumentsToolbar(value?.nativeElement);
    }

    async initParams(): Promise<boolean> {
        this.init();
        await this.reload();
        return true;
    }

    init(): void {
        this.parentId ??= "";

        this.exportMenuItems = [
            {
                label: this.app.t("documents.exportAsCsv"),
                command: this.exportCsv.bind(this)
            },
            {
                label: this.app.t("documents.exportAsExcel"),
                command: this.exportXlsx.bind(this)
            }
        ];
    }

    override destroy(): void {
        this.toolbarResizeObserver?.disconnect();
        this.toolbarMutationObserver?.disconnect();
        if (this.toolbarMeasureAnimationFrame != null) {
            cancelAnimationFrame(this.toolbarMeasureAnimationFrame);
        }
    }

    private initDocumentsToolbar(element: HTMLElement): void {
        this.toolbarResizeObserver?.disconnect();
        this.toolbarMutationObserver?.disconnect();
        this.documentsToolbarElement = element;
        if (element == null) {
            return;
        }

        this.toolbarResizeObserver = new ResizeObserver(() => this.scheduleToolbarMeasurement());
        this.toolbarResizeObserver.observe(element);
        this.toolbarMutationObserver = new MutationObserver(() => this.scheduleToolbarMeasurement());
        this.toolbarMutationObserver.observe(element, { childList: true, characterData: true, subtree: true });
        this.scheduleToolbarMeasurement();
    }

    private scheduleToolbarMeasurement(): void {
        if (this.toolbarMeasureAnimationFrame != null) {
            return;
        }
        this.toolbarMeasureAnimationFrame = requestAnimationFrame(() => {
            this.toolbarMeasureAnimationFrame = null;
            this.updateToolbarCompactState();
        });
    }

    private updateToolbarCompactState(): void {
        const toolbarContent = this.documentsToolbarElement?.querySelector<HTMLElement>(".toolbar-div");
        if (toolbarContent == null || toolbarContent.clientWidth == 0) {
            return;
        }

        const measurement = toolbarContent.cloneNode(true) as HTMLElement;
        measurement.style.position = "fixed";
        measurement.style.left = "-100000px";
        measurement.style.top = "0";
        measurement.style.width = "max-content";
        measurement.style.maxWidth = "none";
        measurement.style.flexWrap = "nowrap";
        measurement.style.overflow = "visible";
        measurement.style.visibility = "hidden";
        document.body.appendChild(measurement);
        const compact = Math.ceil(measurement.scrollWidth) > Math.floor(toolbarContent.clientWidth);
        measurement.remove();

        if (this.toolbarCompact != compact) {
            this.toolbarCompact = compact;
            this.changeDetectorRef.detectChanges();
        }
    }

    async reload(updateResult?: (result: EnaioDocumentFrontendResult) => void, inplace = false): Promise<void> {
        if (this.isAnonymous()) {
            this.documentTypeOptions = [];
            this.def = await RestEndpoint.main().query({ anonymousGuid: this.anonymousGuid }).run("api/doc/def/anonymousresults").get(EnaioDocumentFrontendResult);
        }
        else {
            if (this.definitionInfos.length == 0) {
                this.definitionInfos = await RestEndpoint.main().query({ includeResultCounts: false }).run("api/doc/def").list(EnaioDocumentFrontendResult);
            }
            this.def = this.definitionInfos.find(def => def.defId == this.defId);
            this.updateDocumentTypeOptions();

            /*if (this.def.tokenBased) {
                if (!Utils.isJwtValid(this.parentId)) {
                    this.invalidToken = true;
                    return;
                }
            }*/

            this.searchFields = (this.def.searchFields ?? []).map(sf => new FrontendField(sf.id, sf.title, FrontendFieldType.fromGenericDataType(sf.type), { value: sf.getDefaultValueForForm()}));
            this.def = await RestEndpoint.main().query(Objects.removeNullValues({ defId: this.defId, parentId: this.parentId,
                queryOptions: this.queryOptions != null ? Objects.toJson(this.queryOptions) : Objects.toJson(this.getQueryOptions()) })).run("api/doc/def/search").get(EnaioDocumentFrontendResult);
        }
        this.def.translate(this.app);

        //PRAGMATIC
        if (this.app.mainConfig.customer == "lemgo" && (this.app.auth.userDetails.getDatabaseUser().getAttributeValueSafe("enaioAllowedPersonellIds") ?? "").split(",").length < 2) {
            this.def.columns = this.def.columns.filter(col => col.id != "Mitarbeiter");
        }


        if (updateResult != null) {
            updateResult(this.def);
        }

        this.defId = this.def.defId; //Überschreiben, weil bei AnonymousSearch die defID vorher nicht zwingend feststeht

        let selIds = this.selection == null ? [] : this.selection.map(row => row.id);
        this.updateTable();
        this.selection = this.allRows.filter(row => selIds.includes(row.id));

        if (!inplace) {
            this.rowSelect(null);
        }

        if (!this.embeddedComponent) {
            this.app.updateNavigation(this.app.t("documents.title") + " - " + this.def.name, [
                { label: this.app.t("documents.title"), routerLink: ["/enaio-documents/home"] },
                Utils.isNoe(this.parentId) ? 
                    { label: this.def.name, routerLink: ["/enaio-documents/definition", this.defId] } : 
                    { label: this.def.nameSingular, routerLink: ["/enaio-documents/definition", this.defId, this.parentId] }
            ]);
        }
    }

    updateDocumentTypeOptions(): void {
        if (Utils.isNoe(this.def?.documentTypeSelectionGroup)) {
            this.documentTypeOptions = [];
            return;
        }

        let definitions = this.definitionInfos.filter(def => def.documentTypeSelectionGroup == this.def.documentTypeSelectionGroup);
        if (this.selectableDefinitionIds != null) {
            definitions = definitions.filter(def => this.selectableDefinitionIds.includes(def.defId));
        }
        this.documentTypeOptions = Arrays.sortBy(definitions.map(def => new FrontendFieldListItem(def.defId,
            def.getDisplayName(this.app))), item => item.label);
    }

    async documentTypeChanged(): Promise<void> {
        this.defIdChange.emit(this.defId);
        this.fullTextSearchTerm = "";
        this.input = "";
        this.selection = [];
        this.selectedDocument = null;
        this.selectedDocuments = [];
        this.itemOpened = false;

        if (this.embeddedComponent) {
            await this.reload();
        }
        else {
            const route = ["/enaio-documents", "definition", this.defId];
            if (!Utils.isNoe(this.parentId)) {
                route.push(this.parentId);
            }
            await this.app.navigateTo(route, true);
        }
    }

    updateTable(): void {
        this.table = new TableData(Arrays.withoutNull([
                this.def.displayObjectTypeColumn ? new TableColumn("_objectType", "Objekttyp", TableCellType.string, 20) : null,
                ...this.def.columns.map(col => new TableColumn(col.id, col.title, col.toTableCellType(), -1))
            ]),
            this.def.documents.map(doc => new TableRow(Objects.toString(doc.doc.id), doc, {
                _objectType: doc.doc.objectType.name, 
                ...Arrays.toOrdinaryObject(this.def.columns, col => col.id, col => col.toTableValue(doc.columns[col.id])),
                _download: [new TableCellButton("download", "fas fa-download", "Herunterladen")],
                _open: [new TableCellButton("open", "fas fa-square-up-right", "Öffnen")],
                _seen: doc.seen
            })), 
            this.def.sortColumns?.map(col => new TableSortColumn(col.id, col.ascending)));

        if (this.def.childDefId !== "") {
            this.table.columns.splice(0, 0, new TableColumn("_open", "", TableCellType.button, 1));
        }

        if (this.def.hasDocuments) {
            this.table.columns.splice(0, 0, new TableColumn("_download", "", TableCellType.button, 1));
        }

        if (this.def.useSeenMarker && !this.def.seenMarkerImplicit) {
            this.table.columns.splice(0, 0, new TableColumn("_seen", "", TableCellType.bool, 1, {
                filterable: false,
                editable: true,
                icon: "fas fa-eye"
            }));
        }

        this.allRows = this.table.rows;
        this.updateFilter();
    }

    isAnonymous(): boolean {
        return !Utils.isNoe(this.anonymousGuid);
    }

    formValueToQueryValue(o: any): any {
        return o instanceof Date ? Dates.formatDefaultDate(o) : o;
    }

    getQueryOptions(): OrdinaryObject {
        return Arrays.toOrdinaryObject(this.searchFields, sf => sf.id, sf => this.formValueToQueryValue(sf.getValue()));
    }

    async searchDocuments(): Promise<void> {
        let searchResult = await RestEndpoint.main().query({ defId: this.defId, parentId: this.parentId, queryOptions: Objects.toJson(this.getQueryOptions()), quickSearch: this.input }).run("api/doc/def/search").get(EnaioDocumentFrontendResult);

        if (this.def.useSearchBar) {
            if (searchResult.documents.length == 0) {
                this.app.showToast("info", this.app.t("documents.notFound_title"), this.app.t("documents.notFound", { name: searchResult.nameSingular }));
                return;
            }
            if (searchResult.documents.length > 1) {
                this.app.showToast("info", this.app.t("documents.foundMultiple_title"), this.app.t("documents.foundMultiple", { name: searchResult.name }));
                return;
            }

            await this.app.navigateTo(["/enaio-documents", "definition", this.def.childDefId, searchResult.documents[0].doc.id]);
        }
        else {
            this.def = searchResult;
            this.updateTable();
        }
    }

    async newDocument(_): Promise<void> {
        if (!Utils.isNoe(this.def.writeDefinitionId)) {
            if (await this.documentWriteService.openDialog(this.def.writeDefinitionId, this)) {
                await this.reload();
            }
            return;
        }

        let form: FrontendForm;
        if (this.app.mainConfig.customer == "lemgo") {
            form = new FrontendForm([
                new FrontendField("betreff_notiz", "Notiz", FrontendFieldType.textArea, {
                    mandatory: true
                })]);
        }
        else {
            form = new FrontendForm([
                new FrontendField("title", "Titel", FrontendFieldType.string),
                new FrontendField("document_number", "DokumentNr", FrontendFieldType.string),
                new FrontendField("doc_type", "Dokumenttyp", FrontendFieldType.dropdown, {
                    listItems: FrontendFieldListItem.make([
                            "Allgemeine Beschreibung", 
                            "Verfahrensanweisung", 
                            "Arbeitsanweisung", 
                            "Formular", 
                            "Checkliste", 
                            "Ausgabe an Kunden",
                            "Weitere Unterlagen", 
                            "Anforderungen", 
                            "Betriebsanweisung", 
                            "Sicherheitsdatenblatt", 
                            "Aushang", 
                            "Organigramm", 
                            "Stellenbeschreibung", 
                            "gemeinsame Verantwortung", 
                            "ADV", 
                            "TOM", 
                            "DSFA"
                ])})]);
        }
        form.fields.push(new FrontendField("document", "Dokument hochladen", FrontendFieldType.fileUpload, { mandatory: true }));

        if (await this.app.dialog.form(form, this.app.t("enaio.indexData")) != "ok") {
            return;
        }

        let insert = new EnaioFrontendInsertData();
        insert.defId = this.def.defId;
        insert.parentId = Utils.toNumber(this.parentId);
        insert.fields = [];
        for (let field of form.fields) {
            if (field.id == "document") {
                continue;
            }
            insert.fields.push(new EnaioAssignment(EnaioObjectId.byInternalName(field.id), field.value));
        }
        insert.documentBase64 = Strings.bytesToBase64((form.getValue("document") as BrowserUploadedFile[])[0].contents);
        insert.documentFileName = (form.getValue("document") as BrowserUploadedFile[])[0].legacy.name;
        if ((await this.app.saveDataHandler2(new SaveDataHandler({
            componentView: this,
            worker: async () => RestEndpoint.main().post().body(insert).run("api/doc/def/document").getText(),
            successText: "Das Dokument wurde erfolgreich erstellt."    
        }))).ok) {
            await this.reload();    
        }
    }

    rowSelect(event): void {
        this.selectedDocument = event?.raw;
        if (this.selectedDocument != null) {
            this.itemOpened = true;
            this.remarkObjects = (this.selectedDocument.remarkObjects ?? []).map(doc => {
                let doc2 = Objects.fromPlain(EnaioDocumentResult, doc);
                let name = doc2.doc.objectType?.getBestDisplayName();
                let add = "";
                //PRAGMATIC (zukünftig konfigurierbar)
                if (name == "Reisekostenbeleg") {
                    let note = doc2.doc.getStringByInternal("Bemerkung");
                    add = " - " + doc2.doc.getStringByInternal("Typ") + (Utils.isNoe(note) ? "" : " - " + note);
                }
                return new FrontendFieldListItem(doc.token, Objects.toString((name ?? "???") + add));
            });
            if (Utils.isNoe(this.remarkObjects)) {
                this.remarkObjects = null;
            }
            else {
                this.remarkObjects = [new FrontendFieldListItem(this.selectedDocument.token, "Hauptdokument"), ...this.remarkObjects];
            }
        }
        else {
            this.remarkObjects = null;
        }
    }

    async documentLoaded(token: string): Promise<void> {
        if (this.selectedDocument?.token == token && this.def.useSeenMarker && this.def.seenMarkerImplicit) {
            await this.markDocumentsAsSeen([this.selectedDocument]);
        }
    }

    async cellClicked(event: TableCellEvent): Promise<void> {
        if (event.isButton("open", "_open")) {
            await this.open((event.row.raw as EnaioDocumentResult).doc.id);
        }
        else if (event.isButton("download", "_download")) {
            const doc = event.row.raw as EnaioDocumentResult;
            if (await this.downloadDocument(doc.token) && this.def.useSeenMarker && this.def.seenMarkerImplicit) {
                await this.markDocumentsAsSeen([doc]);
            }
        }
    }

    async markAllAsSeen(): Promise<void> {
        await this.markDocumentsAsSeen(this.table.rows.map(row => row.raw as EnaioDocumentResult));
    }

    async cellEdited(event: TableCellEvent): Promise<void> {
        if (event.column.colId != "_seen") {
            return;
        }

        const doc = event.row.raw as EnaioDocumentResult;
        const previousValue = doc.seen;
        const newValue = Boolean(event.row.values._seen);
        const result = await this.app.genericDataHandler(async () => RestEndpoint.main().post().query({ defId: this.defId, seen: newValue }).body([doc.token]).run("api/doc/def/markSeen").getText(),
            this.app.t("documents.markSeenFailed"), "toast");
        if (result.ok) {
            doc.seen = newValue;
        }
        else {
            event.row.values._seen = previousValue;
        }
        this.updateFilter();
    }

    private async markDocumentsAsSeen(documents: EnaioDocumentResult[]): Promise<boolean> {
        const unseenDocuments = documents.filter(doc => doc != null && !doc.seen && !this.markSeenTokens.has(doc.token));
        if (unseenDocuments.length == 0) {
            return true;
        }

        for (const doc of unseenDocuments) {
            this.markSeenTokens.add(doc.token);
        }
        try {
            for (let index = 0; index < unseenDocuments.length; index += 500) {
                const part = unseenDocuments.slice(index, index + 500);
                const result = await this.app.genericDataHandler(async () => RestEndpoint.main().post().query({ defId: this.defId, seen: true }).body(part.map(doc => doc.token)).run("api/doc/def/markSeen").getText(),
                    this.app.t("documents.markSeenFailed"), "toast");
                if (!result.ok) {
                    return false;
                }
                for (const doc of part) {
                    doc.seen = true;
                    const row = this.allRows.find(item => item.raw == doc);
                    if (row != null) {
                        row.values._seen = true;
                    }
                }
            }
            this.updateFilter();
            return true;
        }
        finally {
            for (const doc of unseenDocuments) {
                this.markSeenTokens.delete(doc.token);
            }
        }
    }

    async open(id: number): Promise<void> {
        await this.app.navigateTo(["/enaio-documents", "definition", this.def.childDefId, Objects.toString(id)]);
    }

    async downloadDocument(token: string): Promise<boolean> {
        const result = await this.app.genericDataHandler(async () => RestEndpoint.main().query({token, convert: 0}).runSaveAsSafe("api/doc/document/document"), this.app.t("documents.downloadFailed"), "toast");
        return result.ok;
    }

    anyRowsVisible(): boolean {
        return this.tDocuments.getUnfilteredRows().length > 0;
    }

    async downloadAll(): Promise<void> {
        await this.buttonClick("downloadAll", async () => {
            if (!this.anyRowsVisible()) {
                this.app.showErrorToast(this.app.t("documents.noVisibleDocuments"));
                return;
            }

            const rows = this.tDocuments.getUnfilteredRows();
            this.multiDownloadRunning = true;
            this.multiDownloadProgress = new Progressor();
            this.multiDownloadProgress.text = this.app.t("documents.downloadPreparing");
            try {
                const result = await this.app.genericDataHandler(async () => {
                    const guid = await RestEndpoint.main().post().body(rows.map(row => (row.raw as EnaioDocumentResult).token)).run("api/doc/document/getMulti").getText();
                    const progressorResult = await ProgressorClient.waitForResult(guid, progressor => { this.multiDownloadProgress = progressor; });
                    const downloadResult = Objects.fromJson(progressorResult.value) as MultiDownloadResult;

                    this.multiDownloadProgress = new Progressor();
                    this.multiDownloadProgress.text = this.app.t("documents.downloadPreparing");
                    await RestEndpoint.main().post().body(downloadResult.token).runSaveAsWithProgress("api/app/safeDownload", downloadResult.fileName, progress => this.updateDownloadProgress(progress));
                    return downloadResult;
                }, this.app.t("documents.downloadFailed"), "toast");
                if (!result.ok) {
                    return;
                }

                const {requested, successful, skipped, failed} = result.value;
                const messages = [this.app.t("documents.multiDownloadSuccess", {successful, requested})];
                if (skipped > 0) {
                    messages.push(this.app.t("documents.multiDownloadSkipped", {count: skipped}));
                }
                if (failed > 0) {
                    messages.push(this.app.t("documents.multiDownloadFailed", {count: failed}));
                }
                this.app.showToast(skipped > 0 || failed > 0 ? "warn" : "success", this.app.t("documents.multiDownloadResultTitle"), messages.join("\n"));

                if (successful == requested && skipped == 0 && failed == 0 && this.def.useSeenMarker && this.def.seenMarkerImplicit) {
                    await this.markDocumentsAsSeen(rows.map(row => row.raw as EnaioDocumentResult));
                }
            }
            finally {
                this.multiDownloadRunning = false;
                this.multiDownloadProgress = null;
            }
        });
    }

    getMultiDownloadProgressText(): string {
        const progressor = this.multiDownloadProgress;
        if (progressor?.text?.startsWith("documents.")) {
            return this.app.t(progressor.text, {processed: Math.min(progressor.value, progressor.max - 1), total: progressor.max - 1});
        }
        return progressor?.text ?? this.app.t("documents.downloadPreparing");
    }

    getMultiDownloadProgressValue(): number {
        return this.multiDownloadProgress?.max > 0 ? Math.round(this.multiDownloadProgress.value * 100 / this.multiDownloadProgress.max) : 0;
    }

    private updateDownloadProgress(progress: number): void {
        this.multiDownloadProgress.max = progress == null ? 0 : 100;
        this.multiDownloadProgress.value = progress ?? 0;
        this.multiDownloadProgress.text = progress == null ? this.app.t("documents.downloadPreparing") :
            this.app.t("documents.downloadProgress", {progress});
    }

    async filterChanged(): Promise<void> {
        await this.searchDocuments();
    }

    updateFilter(): void {
        this.tDocuments.setQuickFilterText(this.fullTextSearchTerm);
        this.table.rows = this.allRows.filter(row =>
                !this.def.useSeenMarker || this.def.seenMarkerImplicit || this.displaySeen || !(row.raw as EnaioDocumentResult).seen);
        if (this.selectedDocument != null && !this.table.rows.some(row => (row.raw as EnaioDocumentResult).doc.id == this.selectedDocument.doc.id)) {
            this.selectedDocument = null;
        }
    }

    closeItemWindow(visible: boolean): void {
        if (!visible) {
            this.itemOpened = false;
        }
    }

    exportCsv(): void {
        this.tDocuments.exportCsv();
    }

    async exportXlsx(): Promise<void> {
        await this.tDocuments.exportXlsx();
    }

    lastTokenReloaded: Date;
    lastExpiredToken: string;

    async tokenExpired(token: string): Promise<void> {
        if (token == this.lastExpiredToken && this.lastTokenReloaded != null && Dates.diffMinutes(this.lastTokenReloaded, new Date()) <= 5) {
            return;
        }

        this.lastExpiredToken = token;
        this.lastTokenReloaded = new Date();
        const selectedDocumentId = this.selectedDocument?.doc.id;
        await this.reload(null, true);
        const refreshedDocument = this.def.documents.find(doc => doc.doc.id == selectedDocumentId);
        const refreshedRow = this.allRows.find(row => row.raw == refreshedDocument);
        this.selection = refreshedRow == null ? [] : [refreshedRow];
        this.rowSelect(refreshedDocument == null ? null : { raw: refreshedDocument });
    }

}
